export interface PasswordValidationResult {
  isValid: boolean;
  errors: string[];
}

/**
 * Validates password (8+ characters and breach check)
 */
export const validatePassword = async (password: string): Promise<PasswordValidationResult> => {
  const errors: string[] = [];

  // Check if password is provided
  if (!password || !password.trim()) {
    errors.push('Password is required');
    return { isValid: false, errors };
  }

  // Basic length check (8+ characters as requested)
  if (password.length < 8) {
    errors.push('Password must be at least 8 characters long');
  }

  // Check for uppercase letter
  if (!/[A-Z]/.test(password)) {
    errors.push('Password must contain at least one uppercase letter');
  }

  // Check for lowercase letter
  if (!/[a-z]/.test(password)) {
    errors.push('Password must contain at least one lowercase letter');
  }

  // Check for number
  if (!/\d/.test(password)) {
    errors.push('Password must contain at least one number');
  }

  // Check for breach using HaveIBeenPwned API with timeout
  try {
    const timeoutPromise = new Promise((_, reject) => 
      setTimeout(() => reject(new Error('Timeout')), 5000)
    );
    
    const isBreached = await Promise.race([
      checkPasswordBreach(password),
      timeoutPromise
    ]) as boolean;
    
    if (isBreached) {
      errors.push('This password has been found in a data breach. Please choose a different password');
    }
  } catch (error) {
    console.warn('Could not check password breach status:', error);
    // Don't block signup if breach check fails
  }

  return {
    isValid: errors.length === 0,
    errors
  };
};

/**
 * Creates SHA-1 hash using Web Crypto API
 */
const sha1Hash = async (text: string): Promise<string> => {
  const encoder = new TextEncoder();
  const data = encoder.encode(text);
  const hashBuffer = await crypto.subtle.digest('SHA-1', data);
  const hashArray = Array.from(new Uint8Array(hashBuffer));
  const hashHex = hashArray.map(b => b.toString(16).padStart(2, '0')).join('');
  return hashHex.toUpperCase();
};

/**
 * Checks if password appears in known data breaches using HaveIBeenPwned API
 */
export const checkPasswordBreach = async (password: string): Promise<boolean> => {
  try {
    // Create SHA-1 hash of password using Web Crypto API
    const hash = await sha1Hash(password);
    
    // Get first 5 characters for k-anonymity
    const prefix = hash.substring(0, 5);
    const suffix = hash.substring(5);

    // Query HaveIBeenPwned API
    const response = await fetch(`https://api.pwnedpasswords.com/range/${prefix}`, {
      method: 'GET',
      headers: {
        'User-Agent': 'Silas-PasswordCheck'
      }
    });

    if (!response.ok) {
      throw new Error(`API request failed: ${response.status}`);
    }

    const data = await response.text();
    
    // Check if our password hash suffix appears in the results
    const lines = data.split('\n');
    for (const line of lines) {
      const [hashSuffix] = line.split(':');
      if (hashSuffix === suffix) {
        return true; // Password found in breach
      }
    }

    return false; // Password not found in any breach
  } catch (error) {
    console.error('Error checking password breach:', error);
    throw error;
  }
};

/**
 * Validates email format
 */
export const validateEmail = (email: string): { isValid: boolean; error?: string } => {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  
  if (!email.trim()) {
    return { isValid: false, error: 'Email address is required' };
  }
  
  if (!emailRegex.test(email)) {
    return { isValid: false, error: 'Please enter a valid email address' };
  }
  
  return { isValid: true };
}; 